import { reportComponentValidationErrorEvent } from "@amx/ux-twoui-telemetry";
import { getId } from "@fluentui/react";
import React, { useEffect } from "react";
import { ShimmerProgressView } from "../components/Shimmer/ShimmerProgressView/ShimmerProgressView";

/**
 * For custom wrapped, but valid child components, provide this HOC with the
 * list of valid components + the parent component name, and then export an HOC
 * that accepts the wrapped component, and then calls the component-specific
 * validation HOC.
 *
 * It takes two generic types, as there are cases where the component returned
 * has a different type to that of the component given (see List for an example)
 *
 * Then wrap the component in the component-specific HOC, which validates the
 * rendered content of the component and marks the component itself as valid.
 * @param element The react node to validate
 * @param validChildComponents The list of valid components
 * @param parentComponentName The name of the component doing the validating -
 * needed for descriptive error messages
 */
export function validChildWrapperComponent<T extends {}, T2 extends T>(
    component: React.FC<T>,
    validChildComponents: readonly React.ComponentType<any>[],
    parentComponentName: string
): React.FC<T2> {
    const wrapper: React.FC<T2> = (...args) => {
        const response = component(...args);
        return validateChildComponent(response, validChildComponents, parentComponentName);
    };
    // Use defineProperty to ignore writes to the displayName, since StoryBook
    // tries to overwrite it
    Object.defineProperty(wrapper, "displayName", {
        enumerable: true,
        configurable: true,
        get: () => `${component.displayName || component.name}_${validationToken(parentComponentName)}`,
        set: () => undefined
    });
    return wrapper;
}

/**
 * Returns the element if provided with a valid list-item element; otherwise
 * throws an Error
 * @param element The react node to validate
 * @param validChildComponents The list of valid components
 * @param parentComponentName The name of the component doing the validating -
 * needed for descriptive error messages
 */
export const validateChildComponent = (
    element: React.ReactNode,
    validChildComponents: readonly React.ComponentType<any>[],
    parentComponentName: string
): JSX.Element => {
    const componentName = elementComponentName(element as JSX.Element);

    if (isReactComponentType(element)) {
        const validationRegex = validationTokenRegex(parentComponentName);
        if (validationRegex.test(componentName) || validChildComponents.includes(element.type)) {
            return element;
        }

        // If the element is a ShimmerProgressView then we validate the different views passed to it.
        // For example, you can find a validated ShimmerProgressView of the Feature component in the ExpandedFeatureGroup stories.
        if (element.type === ShimmerProgressView) {
            const props = element["props"];
            const propNames = Object.keys(props);

            propNames.forEach(propName => {
                if (propName !== "operationData") {
                    const propElement = props[propName];
                    validateChildComponent(propElement, validChildComponents, parentComponentName);
                }
            });
            return element;
        }
    }

    // Will throw if in test/development, but only report an error in prod
    throwOrReportComponentValidationError(componentName, `Invalid ${parentComponentName} component ${componentName}.` +
        ` Use an allowed child component or a custom component wrapped using the childWrapper HOC defined in ${parentComponentName}`, 1);

    // Still return / render the component in prod, but with the knowledge that the error was reported
    return element as JSX.Element;
};

/**
 * Returns the element if it is not one of the invalid child elements; otherwise
 * throws an Error
 * @param element The react node to validate
 * @param validChildComponents The list of valid components
 * @param parentComponentName The name of the component doing the validating -
 * needed for descriptive error messages
 */
export const invalidateChildComponent = (
    element: React.ReactNode,
    invalidChildComponents: readonly React.ComponentType<any>[],
    parentComponentName: string
): JSX.Element => {
    const componentName = elementComponentName(element as JSX.Element);

    if (isReactComponentType(element)) {
        const validationRegex = validationTokenRegex(parentComponentName);

        if (validationRegex.test(componentName) || !invalidChildComponents.includes(element.type)) {
            return element;
        }
    } else {
        return element as JSX.Element;
    }

    // Will throw if in test/development, but only report an error in prod
    throwOrReportComponentValidationError(componentName, `Invalid ${parentComponentName} component ${componentName}.` +
        ` Use an allowed child component or a custom component wrapped using the childWrapper HOC defined in ${parentComponentName}`, 1);

    // Still return / render the component in prod, but with the knowledge that the error was reported
    return element as JSX.Element;
};

/**
 * React hook used to validate that the element provided is a valid child component
 * for the provided parent; otherwise throws an Error.
 * @param element The react node to validate
 * @param validChildComponents The list of valid components
 * @param parentComponentName The name of the component doing the validating -
 * needed for descriptive error messages
 */
export const useChildComponentValidator = (
    element: React.ReactNode,
    validChildComponents: readonly React.ComponentType<any>[],
    parentComponentName: string
) => {

    useEffect(() => {
        validateChildComponent(element, validChildComponents, parentComponentName);
    }, [element, validChildComponents, parentComponentName]);
};

const validationId = getId("Validation");
const validationToken = (parentComponentName: string) => `${parentComponentName}${validationId}`;
const validationTokenRegex = (parentComponentName: string) => new RegExp(`_${validationToken(parentComponentName)}$`);

const isReactComponentType = (element: React.ReactNode): element is React.ReactElement<{}, React.ComponentType> => (
    !!element && React.isValidElement(element) && typeof element.type !== "string"
);

// Getting at the element's name from the complex React JSX.Element type
export const elementComponentName = (element: JSX.Element): string => {
    const elementType = element?.type?.type || element?.type;
    return `${elementType ? elementType.displayName || elementType.name || elementType : element}`;
};

/**
 * A callback which takes no params and returns undefined if the invariant is passed, and an error message
 * if the invariant is failed
 */
export type ComponentValidator = () => string | undefined;

/**
 * A method, similar to `validateProps`, that's intended to be called at the top of a component's render function to
 * validate all of a component's runtime invariants.It takes in the component name and its validators,
 * and if any of the validators fail, it'll throw/report error corresponding to the failed invariant.
 * @param componentName name of the component that's being validated
 * @param componentValidators array of callbacks that will tell us whether the component is passing or failing
 * our invariants. If no validators are passed, it's assumed that the component has no runtime restrictions
 */
export const validateComponent = (componentName: string, componentValidators?: readonly ComponentValidator[]) => {
    if (componentValidators === undefined) {
        // could log "render success" events here
        // componentWasRendered(componentName)
        return;
    }

    const errorMessages: string[] = [];
    componentValidators.forEach((validator: ComponentValidator) => {
        const validationResult = validator();
        if (validationResult) {
            errorMessages.push(validationResult);
        }
    });

    if (errorMessages.length !== 0) {
        throwOrReportComponentValidationError(componentName, errorMessages.join(", "), errorMessages.length);
    } else {
        // could log "render success" events here
        // componentWasRendered(componentName)
    }
};

/**
 * Depending on the process's environment, this method will either throw (test/development) or just report (production)
 * the error(s) encountered by the component.
 * @param componentName name of the component which failed its invariant
 * @param errorMessage descriptive error message for the failed invariant
 * @param errorCount number of errors encountered by the component when it failed. Usually 1, but there are cases
 * where a component can fail multiple invariants at once
 */
export const throwOrReportComponentValidationError = (componentName: string, errorMessage: string, errorCount: number): void => {
    if (process.env.NODE_ENV === "production") {
        reportComponentValidationErrorEvent(componentName, errorMessage, errorCount);
    } else {
        // tslint:disable-next-line: no-throw
        throw Error(errorMessage);
    }
};
